'use client'

import {
  createContext,
  useContext,
  useCallback,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

const STORAGE_PREFIX = 'ps_unlock_'

function getStorageKey(examSlug: string): string {
  return `${STORAGE_PREFIX}${examSlug}`
}

/**
 * Unlock state lives in localStorage; this revision counter is the external
 * store consumers subscribe to. Bumped on every local unlock and on storage
 * events from other tabs, so `isUnlocked` re-reads localStorage exactly when
 * it may have changed.
 */
let revision = 0
const listeners = new Set<() => void>()

function subscribe(callback: () => void): () => void {
  listeners.add(callback)

  const onStorage = (e: StorageEvent) => {
    if (e.key?.startsWith(STORAGE_PREFIX)) {
      revision += 1
      callback()
    }
  }
  window.addEventListener('storage', onStorage)

  return () => {
    listeners.delete(callback)
    window.removeEventListener('storage', onStorage)
  }
}

function emitChange(): void {
  revision += 1
  for (const listener of listeners) {
    listener()
  }
}

function getSnapshot(): number {
  return revision
}

/** Sentinel: during SSR and the hydration pass every gate renders locked. */
function getServerSnapshot(): number {
  return -1
}

interface UnlockContextValue {
  isUnlocked: (examSlug: string) => boolean
  unlock: (examSlug: string, token: string) => void
}

const UnlockContext = createContext<UnlockContextValue | null>(null)

/**
 * Drops a stale unlock (e.g. the server rejected its token) and re-locks
 * every gate immediately. Module-level so non-hook code paths like the
 * download helper can call it.
 */
export function relockExam(examSlug: string): void {
  try {
    localStorage.removeItem(getStorageKey(examSlug))
  } catch {
    // storage unavailable — consumers still re-check via the change event
  }
  emitChange()
}

export function UnlockProvider({ children }: { children: ReactNode }) {
  const rev = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  const isUnlocked = useCallback(
    (examSlug: string): boolean => {
      if (rev < 0) return false
      try {
        const token = localStorage.getItem(getStorageKey(examSlug))
        return token !== null && token.length > 0
      } catch {
        return false
      }
    },
    [rev]
  )

  const unlock = useCallback((examSlug: string, token: string) => {
    try {
      localStorage.setItem(getStorageKey(examSlug), token)
    } catch {
      // storage unavailable — consumers still re-check via the change event
    }
    emitChange()
  }, [])

  return (
    <UnlockContext.Provider value={{ isUnlocked, unlock }}>
      {children}
    </UnlockContext.Provider>
  )
}

export function useUnlock(): UnlockContextValue {
  const ctx = useContext(UnlockContext)
  if (!ctx) {
    throw new Error('useUnlock must be used within <UnlockProvider>')
  }
  return ctx
}
